/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { PureComponent } from 'react';
import get from 'lodash/get';
import { Button, Breadcrumbs, Icon, Heading } from 'sarsaparilla';
import { withCompatRouter } from 'sarsaparilla/legacy';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';

import ErrorNotification from '../ErrorNotification';
import { type Permit } from '../../utils/lottery-schedule-utils';

type PropTypes = {
    permitID?: string;
    permit: Permit;
    recarea: any;
    permitError: string;
    recareaError: string;
    dailyLotteriesError: string;
    router?: {
        navigate: (url: string) => void;
    };
};

class LotteryScheduleHeader extends PureComponent<PropTypes> {
    errorsRef: {
        current: null | HTMLDivElement;
    };

    constructor(props: PropTypes) {
        super(props);

        this.errorsRef = React.createRef();
    }

    componentDidMount() {
        const { permitError, recareaError, dailyLotteriesError } = this.props;

        if (permitError || recareaError || dailyLotteriesError) {
            if (this.errorsRef.current) this.errorsRef.current.focus();
        }
    }

    componentDidUpdate(prevProps: PropTypes) {
        const { permitError, recareaError, dailyLotteriesError } = this.props;
        const hasErrors = permitError || recareaError || dailyLotteriesError;
        const errorsHaveChanged =
            permitError !== prevProps.permitError ||
            recareaError !== prevProps.recareaError ||
            dailyLotteriesError !== prevProps.dailyLotteriesError;

        if (hasErrors && errorsHaveChanged && this.errorsRef.current) {
            this.errorsRef.current.focus();
        }
    }

    onBackToFacilityPageClicked = () => {
        const { permitID } = this.props;
        const facilityUrl = permitID ? `/permits/${permitID}` : '/';
        if (this.props.router) {
            this.props.router.navigate(facilityUrl);
            return;
        }
        window.location.assign(facilityUrl);
    };

    render() {
        const {
            permitID,
            permit,
            recarea,
            permitError,
            recareaError,
            dailyLotteriesError,
        } = this.props;
        const permitName = get(permit, 'name');

        const path = [
            { url: '/', title: 'Home' },
            { url: permitID ? `/permits/${permitID}` : undefined, title: permitName },
            { title: 'Lottery Schedule ' },
        ];

        const { name: recAreaName, entity_id: recAreaUrl } = get(recarea, [0], {});
        if (recAreaName && recAreaUrl) {
            path.splice(1, 0, { url: getGatewayUrl(recAreaUrl), title: recAreaName });
        }

        return (
            <>
                <Button
                    appearance="link"
                    role="link"
                    iconBeforeElement={<Icon iconName="arrow-back" />}
                    onClick={this.onBackToFacilityPageClicked}
                    screenReaderTextBefore="Go"
                    className="back-button"
                >
                    Back to Facility page
                </Button>
                <Breadcrumbs path={path} />
                <ErrorNotification
                    innerRef={this.errorsRef}
                    errors={[permitError, recareaError, dailyLotteriesError]}
                    errorHeader="You've encountered the following error(s):"
                />
                <div className="lottery-schedule-title">
                    <Heading headingLevel={1} appearance="h3">
                        Lottery Schedule
                    </Heading>
                    <p>{permitName}</p>
                </div>
            </>
        );
    }
}

export default withCompatRouter(LotteryScheduleHeader);
